/** Cache keys and shared cached loaders. Write-side scopes live in api/client.ts. */
import { createRoot } from "solid-js";
import { api } from "../api/client";
import { cachedFetch, cachedValue, createCachedResource, TTL } from "./cache";

export const calendarKey = (y: number, m: number) =>
  `calendar:${y}-${String(m).padStart(2, "0")}`;

export function currentYearMonth() {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1 };
}

/** Start the current month's calendar request; the calendar page joins it. */
export function prefetchCurrentCalendar() {
  const { y, m } = currentYearMonth();
  return cachedFetch(calendarKey(y, m), () => api.getCalendar(y, m)).catch(
    () => undefined,
  );
}

export const loadTemplates = () =>
  cachedValue("templates", () => api.listTemplates());

export const loadCatalog = () =>
  cachedValue("catalog", () => api.listCatalogExercises(), TTL.long);

let catalogStore: ReturnType<typeof createCatalogStore> | undefined;

function createCatalogStore() {
  return createRoot(() =>
    createCachedResource(() => true, {
      key: () => "catalog",
      fetch: () => api.listCatalogExercises(),
      ttl: TTL.long,
    }),
  );
}

/** One catalog resource for the whole app (loaded on first use, then shared). */
export function useCatalog() {
  catalogStore ??= createCatalogStore();
  return catalogStore;
}

export const recordsKey = (exerciseId: string) => `records:${exerciseId}`;

/** Progress of one catalog exercise over one range (`progress` is invalidated with `records`). */
export { progressKey } from "./progress";
